import {
  Ticket,
  Users,
  Settings,
  ChevronLeft,
  Shield,
  Building2,
  // Inbox,
  Kanban,
  GitMerge,
  Bell,
  LogOut,
  FileText,
  ChevronDown,
  ChevronUp,
  CircleUserRound,
  Contact,
  LayoutDashboard,
  Activity,
  BarChart3,
  FileBarChart,
  FolderKanban,
  X,
  ExternalLink,
  MessageSquare,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { SD_BASE, sd } from "@sd/lib/sdPaths";
import { useAuth } from "@sd/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { MenuService } from "@/services/APIs";
import { useCurrentUserName } from "@sd/hooks/useCurrentUserName";
import { openPtsApp } from "@sd/lib/openPtsApp";

/** Map API path → SD admin path */
function a1PathToSdAdmin(path: string): string {
  if (path === "/") return sd("admin/a1-schemas");
  if (path === "/imports") return sd("admin/a1-imports");
  if (path === "/permission") return sd("admin/a1-permission");
  if (path === "/roles") return sd("admin/a1-roles");
  if (path === "/users") return sd("admin/a1-tenant-users");
  // any other path (e.g. /sla-configurations) — embed under /sd/admin
  return sd(`admin${path}`);
}

/** Service Desk sidebar order: alerts → ticket workflow → companies → people → config */
const adminNavItems = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: sd("admin") },
  { id: "report", label: "Report", icon: FileBarChart, path: sd("admin/report") },
  {
    id: "project-status",
    label: "Dashboard Project Status",
    icon: BarChart3,
    path: sd("admin/project-status"),
  },
  { id: "projects", label: "Projects", icon: FolderKanban, path: sd("admin/projects") },
  { id: "notifications", label: "Notifications", icon: Bell, path: sd("admin/notifications") },
  // { id: "inbox", label: "Email Inbox", icon: Inbox, path: sd("admin/inbox") },
  { id: "tickets", label: "Ticket Management", icon: Ticket, path: sd("admin/tickets") },
  { id: "kanban", label: "Kanban Board", icon: Kanban, path: sd("admin/kanban") },
  { id: "merge", label: "Merge Tickets", icon: GitMerge, path: sd("admin/merge") },
  // { id: "companies", label: "Customer Companies", icon: Building2, path: sd("admin/companies") },
  { id: "employees", label: "Employees", icon: CircleUserRound, path: sd("admin/employees") },
  { id: "guest", label: "Guest", icon: Contact, path: sd("admin/guest") },
  // { id: "users", label: "Users & Agents", icon: Users, path: sd("admin/users") },
  { id: "activity", label: "Activity Log", icon: Activity, path: sd("admin/activity") },
  // { id: "reports", label: "Reports & Analytics", icon: BarChart3, path: sd("admin/reports") },
  { id: "chat", label: "Chat", icon: MessageSquare, path: sd("admin/chat") },
  { id: "settings", label: "Settings", icon: Settings, path: sd("admin/settings") },
];


interface AdminSidebarProps {
  /** Mobile drawer — nav ke baad band karo */
  onNavigate?: () => void;
}

export function AdminSidebar({ onNavigate }: AdminSidebarProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut, role } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const isDrawer = !!onNavigate;

  const goTo = (path: string) => {
    navigate(path);
    onNavigate?.();
  };

  const { displayName, initials } = useCurrentUserName();

  const roleLabel: Record<string, string> = {
    ServiceDesk_Admin: 'Admin',
    ServiceDesk_SuperAgent: 'Super Agent',
    ServiceDesk_Agent: 'Agent',
    ServiceDesk_User: 'End User',
  };

  const { data: menuData = [] } = useQuery({
    queryKey: ["menus"],
    queryFn: () => MenuService.listMenus(),
    staleTime: 5 * 60_000,
  });

  const a1MenuItems: { id: string; title: string; sdPath: string; children?: { id: string; title: string; sdPath: string }[] }[] =
    (Array.isArray(menuData) ? menuData : []).map((item: any) => ({
      id: item.id,
      title: item.title,
      sdPath: a1PathToSdAdmin(item.path ?? ""),
      children: item.children?.length
        ? item.children.map((c: any) => ({
            id: c.id,
            title: c.title,
            sdPath: a1PathToSdAdmin(c.path ?? ""),
          }))
        : undefined,
    }));

  const handleLogout = async () => {
    await signOut();
    onNavigate?.();
    navigate(sd("login"), { replace: true });
  };

  const isActive = (path: string) => {
    const base = sd("admin");
    if (path === base) {
      return location.pathname === base || location.pathname === `${base}/`;
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const toggleGroup = (id: string) => {
    setOpenGroups(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Auto-open groups whose children are active
  useEffect(() => {
    const toOpen = a1MenuItems
      .filter(item => item.children?.some(c => isActive(c.sdPath)))
      .map(item => item.id);
    if (toOpen.length) setOpenGroups(prev => new Set([...prev, ...toOpen]));
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className={cn(
        "flex h-screen flex-col border-r bg-card transition-all duration-300",
        isDrawer ? "w-full" : collapsed ? "w-16" : "w-64"
      )}
    >
      <div className="flex items-center justify-between border-b p-4">
        {(!collapsed || isDrawer) && (
          <div className="flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            <span className="text-lg font-bold text-foreground">Admin Panel</span>
          </div>
        )}
        {collapsed && !isDrawer && <Shield className="mx-auto h-6 w-6 text-primary" />}
        {!isDrawer ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setCollapsed(!collapsed)}
          >
            <ChevronLeft
              className={cn("h-4 w-4 transition-transform", collapsed && "rotate-180")}
            />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => onNavigate?.()}
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* User profile card — top */}
      <div className={cn(
        "shrink-0 border-b bg-muted/30 px-3 py-3",
        collapsed && !isDrawer ? "flex justify-center" : ""
      )}>
        {collapsed && !isDrawer ? (
          <div
            title={displayName ?? 'Admin'}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground"
          >
            {initials}
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-foreground leading-tight">
                {displayName ?? 'Admin'}
              </p>
              <span className="inline-block rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary leading-tight">
                {role ? (roleLabel[role] ?? role) : 'Admin'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Service Desk — scrollable middle */}
      <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto p-2">
        {(!collapsed || isDrawer) && (
          <div className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Service Desk
          </div>
        )}

        {adminNavItems.map(item => (
          <button
            key={item.id}
            type="button"
            title={item.label}
            onClick={() => goTo(item.path)}
            className={cn(
              "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
              isActive(item.path)
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
          >
            <item.icon className="h-5 w-5 flex-shrink-0" />
            {(!collapsed || isDrawer) && <span>{item.label}</span>}
          </button>
        ))}

      </nav>

      {/* Architecture One — API-driven menu */}
      <div className="shrink-0 space-y-1 border-t p-2">
        {(!collapsed || isDrawer) && (
          <div className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Architecture One
          </div>
        )}

        {a1MenuItems.map(item =>
          item.children?.length ? (
            // Group with children
            <div key={item.id}>
              {(!collapsed || isDrawer) ? (
                <>
                  <button
                    type="button"
                    onClick={() => toggleGroup(item.id)}
                    className={cn(
                      "flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                      item.children.some(c => isActive(c.sdPath))
                        ? "bg-primary/15 text-foreground"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <span className="flex items-center gap-3">
                      <Users className="h-5 w-5 flex-shrink-0" />
                      {item.title}
                    </span>
                    {openGroups.has(item.id) ? (
                      <ChevronUp className="h-4 w-4 opacity-70" />
                    ) : (
                      <ChevronDown className="h-4 w-4 opacity-70" />
                    )}
                  </button>
                  {openGroups.has(item.id) && (
                    <div className="ml-2 space-y-0.5 border-l border-border pl-2">
                      {item.children.map(child => (
                        <button
                          key={child.id}
                          type="button"
                          onClick={() => goTo(child.sdPath)}
                          className={cn(
                            "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                            isActive(child.sdPath)
                              ? "bg-primary text-primary-foreground"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          <FileText className="h-4 w-4 flex-shrink-0" />
                          <span>{child.title}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                item.children.map(child => (
                  <button
                    key={child.id}
                    type="button"
                    title={child.title}
                    onClick={() => goTo(child.sdPath)}
                    className={cn(
                      "flex w-full items-center justify-center rounded-lg px-3 py-2.5 transition-colors",
                      isActive(child.sdPath)
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <FileText className="h-5 w-5" />
                  </button>
                ))
              )}
            </div>
          ) : (
            // Single item
            <button
              key={item.id}
              type="button"
              title={item.title}
              onClick={() => goTo(item.sdPath)}
              className={cn(
                "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                isActive(item.sdPath)
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              <FileText className="h-5 w-5 flex-shrink-0" />
              {(!collapsed || isDrawer) && <span>{item.title}</span>}
            </button>
          )
        )}
      </div>

      <div className="shrink-0 space-y-2 border-t p-2">
        <button
          type="button"
          onClick={() => {
            onNavigate?.();
            openPtsApp({ sameTab: false });
          }}
          title="Open PTS"
          className={cn(
            "flex w-full items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/10",
            collapsed && !isDrawer && "justify-center"
          )}
        >
          <ExternalLink className="h-5 w-5 flex-shrink-0" />
          {(!collapsed || isDrawer) && <span>Open PTS</span>}
        </button>
        <button
          type="button"
          onClick={handleLogout}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-destructive transition-colors hover:bg-destructive/10"
        >
          <LogOut className="h-5 w-5 flex-shrink-0" />
          {(!collapsed || isDrawer) && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
}
